// ============================================================================
// Released authenticated-surface boundary
// ============================================================================
//
// A ledger row only says that a candidate MAY take the independent path. This
// module turns that pure answer into a short-lived capability tied to the
// exact lease, subject, routed surface, and path that caused it. App must
// revalidate the capability immediately before rendering; a prior route,
// profile, or session is never enough to keep a release mounted.
//
// It deliberately owns no React state, no query, and no all-data loader. Its
// hand-written raw pairs stay intentionally narrower than the ledger, so an
// added ledger row cannot silently widen a released render claim.
// ============================================================================

import {isCanonicalPigFarrowingLocation, isCanonicalPigFarrowingPathname} from './pigFarrowingPath.ts';
import {
  AUTHENTICATED_SURFACE_FALLBACK,
  AUTHENTICATED_SURFACE_MODE,
  resolveAuthenticatedSurface,
} from './authenticatedSurfaceResolver.ts';
import {BOOT_RELEASE_LEDGER} from './bootReleaseLedger.ts';
import {
  BROILER_DAILYS_DETAIL_PATHNAME_PREFIX,
  BROILER_DAILYS_INDEX_PATHNAME,
  isCanonicalBroilerDailysDetailLocation,
  isCanonicalBroilerDailysDetailPathname,
  isCanonicalBroilerDailysIndexLocation,
  isCanonicalBroilerDailysIndexPathname,
} from './broilerDailysPaths.ts';
import {
  BROILER_BATCHES_DETAIL_PATHNAME_PREFIX,
  BROILER_BATCHES_INDEX_PATHNAME,
  isCanonicalBroilerBatchesDetailLocation,
  isCanonicalBroilerBatchesDetailPathname,
  isCanonicalBroilerBatchesIndexLocation,
  isCanonicalBroilerBatchesIndexPathname,
} from './broilerBatchesPaths.ts';
import {
  CATTLE_BATCHES_DETAIL_PATHNAME_PREFIX,
  isCanonicalCattleBatchesDetailLocation,
  isCanonicalCattleBatchesDetailPathname,
} from './cattleBatchesDetailPath.ts';
import {
  CATTLE_BATCHES_INDEX_PATHNAME,
  isCanonicalCattleBatchesIndexLocation,
  isCanonicalCattleBatchesIndexPathname,
} from './cattleBatchesIndexPath.ts';
import {CATTLE_DAILYS_DETAIL_PATHNAME_PREFIX, isCanonicalCattleDailysDetailPathname} from './cattleDailysDetailPath.ts';
import {FLEET_DETAIL_PATHNAME_PREFIX, isCanonicalFleetDetailPathname} from './fleetDetailPath.ts';
import {
  FLEET_CHECKLIST_ENTRY_PATHNAME_PREFIX,
  FLEET_FUELING_ENTRY_PATHNAME_PREFIX,
  isCanonicalFleetChecklistEntryPathname,
  isCanonicalFleetFuelingEntryPathname,
} from './fleetEntryPath.ts';
import {
  EGG_DAILYS_DETAIL_PATHNAME_PREFIX,
  EGG_DAILYS_INDEX_PATHNAME,
  isCanonicalEggDailysDetailLocation,
  isCanonicalEggDailysDetailPathname,
  isCanonicalEggDailysIndexLocation,
  isCanonicalEggDailysIndexPathname,
} from './eggDailysPaths.ts';
import {
  EQUIPMENT_FUELING_FORM_PATHNAME_PREFIX,
  isCanonicalEquipmentFuelingFormPathname,
} from './equipmentFuelingFormPath.ts';
import {
  CATTLE_FORECAST_BATCH_DETAIL_PATHNAME_PREFIX,
  isCanonicalCattleForecastBatchDetailLocation,
  isCanonicalCattleForecastBatchDetailPathname,
} from './cattleForecastBatchDetailPath.ts';
import {
  CATTLE_HERDS_DETAIL_PATHNAME_PREFIX,
  CATTLE_HERDS_INDEX_PATHNAME,
  isCanonicalCattleHerdsDetailLocation,
  isCanonicalCattleHerdsDetailPathname,
  isCanonicalCattleHerdsIndexLocation,
  isCanonicalCattleHerdsIndexPathname,
} from './cattleHerdsPath.ts';
import {
  FLEET_FUEL_LOG_PATHNAME,
  FLEET_INDEX_PATHNAME,
  isCanonicalFleetIndexLocation,
  isCanonicalFleetIndexPathname,
} from './fleetIndexPath.ts';
import {
  SHEEP_BATCHES_DETAIL_PATHNAME_PREFIX,
  SHEEP_BATCHES_INDEX_PATHNAME,
  SHEEP_FLOCKS_DETAIL_PATHNAME_PREFIX,
  SHEEP_FLOCKS_INDEX_PATHNAME,
  isCanonicalSheepBatchesDetailLocation,
  isCanonicalSheepBatchesDetailPathname,
  isCanonicalSheepFlocksDetailLocation,
  isCanonicalSheepFlocksDetailPathname,
} from './sheepRouterPaths.ts';
import {
  LAYER_DAILYS_DETAIL_PATHNAME_PREFIX,
  LAYER_DAILYS_INDEX_PATHNAME,
  isCanonicalLayerDailysDetailLocation,
  isCanonicalLayerDailysDetailPathname,
  isCanonicalLayerDailysIndexLocation,
  isCanonicalLayerDailysIndexPathname,
} from './layerDailysPaths.ts';
import {
  LAYER_BATCHES_DETAIL_PATHNAME_PREFIX,
  isCanonicalLayerBatchesDetailLocation,
  isCanonicalLayerBatchesDetailPathname,
} from './layerBatchesDetailPath.ts';
import {
  LAYER_HOUSINGS_DETAIL_PATHNAME_PREFIX,
  isCanonicalLayerHousingsDetailLocation,
  isCanonicalLayerHousingsDetailPathname,
} from './layerHousingsDetailPath.ts';
import {
  PIG_DAILYS_DETAIL_PATHNAME_PREFIX,
  PIG_DAILYS_INDEX_PATHNAME,
  isCanonicalPigDailysDetailLocation,
  isCanonicalPigDailysDetailPathname,
  isCanonicalPigDailysIndexLocation,
  isCanonicalPigDailysIndexPathname,
} from './pigDailysPaths.ts';
import {
  PIG_BATCHES_DETAIL_PATHNAME_PREFIX,
  PIG_BATCHES_INDEX_PATHNAME,
  isCanonicalPigBatchesDetailLocation,
  isCanonicalPigBatchesDetailPathname,
  isCanonicalPigBatchesIndexLocation,
  isCanonicalPigBatchesIndexPathname,
} from './pigBatchesPaths.ts';
import {
  SHEEP_DAILYS_DETAIL_PATHNAME_PREFIX,
  SHEEP_DAILYS_INDEX_PATHNAME,
  isCanonicalSheepDailysDetailLocation,
  isCanonicalSheepDailysDetailPathname,
  isCanonicalSheepDailysIndexLocation,
  isCanonicalSheepDailysIndexPathname,
} from './sheepDailysPaths.ts';
import {
  WEIGHIN_SESSION_RECORD_PATHNAME_PREFIX,
  isCanonicalWeighInSessionRecordLocation,
  isCanonicalWeighInSessionRecordPathname,
} from './weighInSessionRecordPath.ts';
import {
  CUT_SHEET_EDITOR_PATHNAME_PREFIX,
  isCanonicalCutSheetEditorLocation,
  isCanonicalCutSheetEditorPathname,
} from './cutSheetRecordPaths.ts';
import {
  ADMIN_EQUIPMENT_RECORD_PATHNAME_PREFIX,
  isCanonicalAdminEquipmentRecordLocation,
  isCanonicalAdminEquipmentRecordPathname,
} from './adminEquipmentRecordPath.ts';
import {
  PIG_SOWS_INDEX_PATHNAME,
  PIG_SOWS_RECORD_PATHNAME_PREFIX,
  isCanonicalPigSowsLocation,
  isCanonicalPigSowsPathname,
} from './pigSowsPath.ts';
import {
  isCanonicalTasksCenterLocation,
  isCanonicalTasksCenterPathname,
  TASKS_CENTER_PATHNAME,
  TASKS_CENTER_TRAILING_PATHNAME,
  TASKS_TODO_PATHNAME,
  TASKS_TODO_TRAILING_PATHNAME,
} from './tasksCenterPath.ts';
import {
  TASKS_INSTANCE_DETAIL_PATHNAME_PREFIX,
  TASKS_TODO_DETAIL_PATHNAME_PREFIX,
  isCanonicalTasksInstanceDetailLocation,
  isCanonicalTasksInstanceDetailPathname,
  isCanonicalTasksTodoDetailLocation,
  isCanonicalTasksTodoDetailPathname,
} from './tasksRecordPaths.ts';
import {
  WEBFORM_HUB_DAILYS_PATHNAME,
  isCanonicalWebformHubDailysLocation,
  isCanonicalWebformHubDailysPathname,
} from './webformHubDailysPath.ts';
import type {
  AuthenticatedSurfaceResolution,
  AuthenticatedSurfaceResolverInput,
} from './authenticatedSurfaceResolver.ts';
import type {LegacyBootLease} from './legacyBootCoordinator.ts';

export const RELEASED_CATTLE_BREEDING_SURFACE = 'view.cattlebreeding';
export const RELEASED_CATTLE_BREEDING_PATHNAME = '/cattle/breeding';
export const RELEASED_CATTLE_DAILYS_INDEX_SURFACE = 'cattle.dailys.index';
export const RELEASED_CATTLE_DAILYS_INDEX_PATHNAME = '/cattle/dailys';
export const RELEASED_MY_SUBMISSIONS_SURFACE = 'view.mySubmissions';
export const RELEASED_MY_SUBMISSIONS_PATHNAME = '/my-submissions';
export const RELEASED_FUEL_SUPPLY_SURFACE = 'view.fuelSupply';
export const RELEASED_FUEL_SUPPLY_PATHNAME = '/fuel-supply';
export const RELEASED_EQUIPMENT_FUELING_SUPPLY_SURFACE = 'equipment.fueling.supply';
export const RELEASED_EQUIPMENT_FUELING_SUPPLY_PATHNAME = '/equipment/supply';
export const RELEASED_EQUIPMENT_FUELING_HUB_SURFACE = 'equipment.fueling.hub';
export const RELEASED_EQUIPMENT_FUELING_HUB_PATHNAME = '/equipment';
export const RELEASED_EQUIPMENT_FUELING_FORM_SURFACE = 'equipment.fueling.form';
export const RELEASED_EQUIPMENT_FUELING_FORM_PATHNAME_PREFIX = EQUIPMENT_FUELING_FORM_PATHNAME_PREFIX;
export const RELEASED_ACTIVITY_SURFACE = 'view.activity';
export const RELEASED_ACTIVITY_PATHNAME = '/activity';
export const RELEASED_PROJECTS_SURFACE = 'view.projects';
export const RELEASED_PROJECTS_PATHNAME = '/projects';
export const RELEASED_ADD_FEED_SURFACE = 'view.addfeed';
export const RELEASED_ADD_FEED_PATHNAME = '/addfeed';
export const RELEASED_NEWSLETTER_ADMIN_SURFACE = 'view.newsletterAdmin';
export const RELEASED_NEWSLETTER_ADMIN_PATHNAME = '/admin/newsletter';
export const RELEASED_CLIENT_ERRORS_SURFACE = 'view.clientErrors';
export const RELEASED_CLIENT_ERRORS_PATHNAME = '/admin/client-errors';
export const RELEASED_WEBFORMS_ADMIN_SURFACE = 'view.webforms';
export const RELEASED_WEBFORMS_ADMIN_PATHNAME = '/admin';
export const RELEASED_PIG_DAILYS_WEBFORM_SURFACE = 'view.webform';
export const RELEASED_PIG_DAILYS_WEBFORM_PATHNAME = '/webform-pigs';
export const RELEASED_WEBFORM_HUB_SURFACE = 'view.webformhub';
export const RELEASED_WEBFORM_HUB_DAILYS_PATHNAME = WEBFORM_HUB_DAILYS_PATHNAME;
export const RELEASED_TASKS_WEBFORM_SURFACE = 'view.tasksWebform';
export const RELEASED_TASKS_WEBFORM_PATHNAME = '/dailys/tasks';
export const RELEASED_PIG_BREEDING_SURFACE = 'view.breeding';
export const RELEASED_PIG_BREEDING_PATHNAME = '/pig/breeding';
export const RELEASED_PIG_SOWS_SURFACE = 'view.sows';
export const RELEASED_PIG_SOWS_INDEX_PATHNAME = PIG_SOWS_INDEX_PATHNAME;
export const RELEASED_PIG_SOWS_RECORD_PATHNAME_PREFIX = PIG_SOWS_RECORD_PATHNAME_PREFIX;
export const RELEASED_PASTURE_MAP_SURFACE = 'view.pastureMap';
export const RELEASED_PASTURE_MAP_PATHNAME = '/pasture-map';
export const RELEASED_CATTLE_LOG_SURFACE = 'view.cattlelog';
export const RELEASED_CATTLE_LOG_PATHNAME = '/cattle/log';
export const RELEASED_PROCESSING_SURFACE = 'view.processing';
export const RELEASED_PROCESSING_PATHNAME = '/processing';
// The cut-sheet editor is a RECORD SPACE beneath the exact `/processing` row,
// so it gets a PREFIX rather than a pathname: every address it owns is
// `/processing/cut-sheet/<recordId>` and there is no bare hub pathname to name.
// The prefix constant is re-exported from the route contract for the same
// reason every other record space re-exports its own — one definition, so the
// boundary and the fence cannot disagree about where the space starts.
export const RELEASED_PROCESSING_CUT_SHEET_SURFACE = 'view.processingCutSheet';
export const RELEASED_PROCESSING_CUT_SHEET_PATHNAME_PREFIX = CUT_SHEET_EDITOR_PATHNAME_PREFIX;
// A40: the Admin equipment record page is the cut-sheet arrangement again — a
// record space with a PREFIX and no bare pathname. Re-exported from the route
// contract so the boundary and the fence share one definition.
export const RELEASED_EQUIPMENT_ADMIN_RECORD_SURFACE = 'view.equipmentAdminRecord';
export const RELEASED_EQUIPMENT_ADMIN_RECORD_PATHNAME_PREFIX = ADMIN_EQUIPMENT_RECORD_PATHNAME_PREFIX;
export const RELEASED_PRODUCTION_SURFACE = 'view.production';
export const RELEASED_PRODUCTION_PATHNAME = '/production';
export const RELEASED_CATTLE_HOME_SURFACE = 'view.cattleHome';
export const RELEASED_CATTLE_HOME_PATHNAME = '/cattle';
export const RELEASED_SHEEP_HOME_SURFACE = 'view.sheepHome';
export const RELEASED_SHEEP_HOME_PATHNAME = '/sheep';
export const RELEASED_BROILER_HOME_SURFACE = 'view.broilerHome';
export const RELEASED_BROILER_HOME_PATHNAME = '/broiler';
export const RELEASED_LAYERS_HOME_SURFACE = 'view.layersHome';
export const RELEASED_LAYERS_HOME_PATHNAME = '/layer';
export const RELEASED_LAYER_GROUPS_SURFACE = 'view.layers';
export const RELEASED_LAYER_GROUPS_PATHNAME = '/layer/groups';
export const RELEASED_PIGS_HOME_SURFACE = 'view.pigsHome';
export const RELEASED_PIGS_HOME_PATHNAME = '/pig';
export const RELEASED_WEATHER_SURFACE = 'view.weather';
export const RELEASED_WEATHER_PATHNAME = '/weather';
export const RELEASED_CATTLE_BATCHES_INDEX_SURFACE = 'cattle.batches.index';
export const RELEASED_CATTLE_BATCHES_INDEX_PATHNAME = CATTLE_BATCHES_INDEX_PATHNAME;
export const RELEASED_CATTLE_BATCHES_DETAIL_SURFACE = 'cattle.batches.detail';
export const RELEASED_CATTLE_BATCHES_DETAIL_PATHNAME_PREFIX = CATTLE_BATCHES_DETAIL_PATHNAME_PREFIX;
export const RELEASED_BROILER_BATCHES_INDEX_SURFACE = 'broiler.batches.index';
export const RELEASED_BROILER_BATCHES_INDEX_PATHNAME = BROILER_BATCHES_INDEX_PATHNAME;
export const RELEASED_BROILER_BATCHES_DETAIL_SURFACE = 'broiler.batches.detail';
export const RELEASED_BROILER_BATCHES_DETAIL_PATHNAME_PREFIX = BROILER_BATCHES_DETAIL_PATHNAME_PREFIX;
export const RELEASED_CATTLE_FORECAST_BATCH_DETAIL_SURFACE = 'cattle.batches.forecastDetail';
export const RELEASED_CATTLE_FORECAST_BATCH_DETAIL_PATHNAME_PREFIX = CATTLE_FORECAST_BATCH_DETAIL_PATHNAME_PREFIX;
export const RELEASED_CATTLE_FORECAST_SURFACE = 'view.cattleforecast';
export const RELEASED_CATTLE_FORECAST_PATHNAME = '/cattle/forecast';
export const RELEASED_LAYER_BATCHES_INDEX_SURFACE = 'layer.batches.index';
export const RELEASED_LAYER_BATCHES_INDEX_PATHNAME = '/layer/batches';
export const RELEASED_LAYER_HOUSINGS_DETAIL_SURFACE = 'layer.housings.detail';
export const RELEASED_LAYER_HOUSINGS_DETAIL_PATHNAME_PREFIX = LAYER_HOUSINGS_DETAIL_PATHNAME_PREFIX;
export const RELEASED_LAYER_BATCHES_DETAIL_SURFACE = 'layer.batches.detail';
export const RELEASED_LAYER_BATCHES_DETAIL_PATHNAME_PREFIX = LAYER_BATCHES_DETAIL_PATHNAME_PREFIX;
export const RELEASED_LAYER_DAILYS_INDEX_SURFACE = 'layer.dailys.index';
export const RELEASED_LAYER_DAILYS_INDEX_PATHNAME = LAYER_DAILYS_INDEX_PATHNAME;
export const RELEASED_LAYER_DAILYS_DETAIL_SURFACE = 'layer.dailys.detail';
export const RELEASED_LAYER_DAILYS_DETAIL_PATHNAME_PREFIX = LAYER_DAILYS_DETAIL_PATHNAME_PREFIX;
export const RELEASED_EQUIPMENT_FLEET_INDEX_SURFACE = 'equipment.fleet.index';
export const RELEASED_EQUIPMENT_FLEET_INDEX_PATHNAME = FLEET_INDEX_PATHNAME;
export const RELEASED_EQUIPMENT_FLEET_FUEL_LOG_PATHNAME = FLEET_FUEL_LOG_PATHNAME;
export const RELEASED_EQUIPMENT_FLEET_DETAIL_SURFACE = 'equipment.fleet.detail';
export const RELEASED_EQUIPMENT_FLEET_DETAIL_PATHNAME_PREFIX = FLEET_DETAIL_PATHNAME_PREFIX;
export const RELEASED_EQUIPMENT_FUELING_ENTRY_SURFACE = 'equipment.fueling.entry';
export const RELEASED_EQUIPMENT_FUELING_ENTRY_PATHNAME_PREFIX = FLEET_FUELING_ENTRY_PATHNAME_PREFIX;
export const RELEASED_EQUIPMENT_CHECKLIST_ENTRY_SURFACE = 'equipment.checklist.entry';
export const RELEASED_EQUIPMENT_CHECKLIST_ENTRY_PATHNAME_PREFIX = FLEET_CHECKLIST_ENTRY_PATHNAME_PREFIX;
export const RELEASED_CATTLE_DAILYS_DETAIL_SURFACE = 'cattle.dailys.detail';
export const RELEASED_CATTLE_DAILYS_DETAIL_PATHNAME_PREFIX = CATTLE_DAILYS_DETAIL_PATHNAME_PREFIX;
export const RELEASED_LIGHT_HOME_SURFACE = 'app.home.light';
export const RELEASED_LIGHT_HOME_PATHNAME = '/';
export const RELEASED_STANDARD_HOME_SURFACE = 'app.home.standard';
export const RELEASED_STANDARD_HOME_PATHNAME = '/';
export const RELEASED_BROILER_WEIGHINS_SURFACE = 'view.broilerweighins';
export const RELEASED_BROILER_WEIGHINS_PATHNAME = '/broiler/weighins';
export const RELEASED_PIG_WEIGHINS_SURFACE = 'view.pigweighins';
export const RELEASED_PIG_WEIGHINS_PATHNAME = '/pig/weighins';
export const RELEASED_CATTLE_WEIGHINS_SURFACE = 'view.cattleweighins';
export const RELEASED_CATTLE_WEIGHINS_PATHNAME = '/cattle/weighins';
export const RELEASED_SHEEP_WEIGHINS_SURFACE = 'view.sheepweighins';
export const RELEASED_SHEEP_WEIGHINS_PATHNAME = '/sheep/weighins';
export const RELEASED_CATTLE_HERDS_INDEX_SURFACE = 'cattle.herds.index';
export const RELEASED_CATTLE_HERDS_INDEX_PATHNAME = CATTLE_HERDS_INDEX_PATHNAME;
export const RELEASED_CATTLE_HERDS_DETAIL_SURFACE = 'cattle.herds.detail';
export const RELEASED_CATTLE_HERDS_DETAIL_PATHNAME_PREFIX = CATTLE_HERDS_DETAIL_PATHNAME_PREFIX;
export const RELEASED_SHEEP_BATCHES_INDEX_SURFACE = 'sheep.batches.index';
export const RELEASED_SHEEP_BATCHES_INDEX_PATHNAME = SHEEP_BATCHES_INDEX_PATHNAME;
export const RELEASED_SHEEP_BATCHES_DETAIL_SURFACE = 'sheep.batches.detail';
export const RELEASED_SHEEP_BATCHES_DETAIL_PATHNAME_PREFIX = SHEEP_BATCHES_DETAIL_PATHNAME_PREFIX;
export const RELEASED_SHEEP_FLOCKS_INDEX_SURFACE = 'sheep.flocks.index';
export const RELEASED_SHEEP_FLOCKS_INDEX_PATHNAME = SHEEP_FLOCKS_INDEX_PATHNAME;
export const RELEASED_SHEEP_FLOCKS_DETAIL_SURFACE = 'sheep.flocks.detail';
export const RELEASED_SHEEP_FLOCKS_DETAIL_PATHNAME_PREFIX = SHEEP_FLOCKS_DETAIL_PATHNAME_PREFIX;
export const RELEASED_SHEEP_DAILYS_INDEX_SURFACE = 'sheep.dailys.index';
export const RELEASED_SHEEP_DAILYS_INDEX_PATHNAME = SHEEP_DAILYS_INDEX_PATHNAME;
export const RELEASED_SHEEP_DAILYS_DETAIL_SURFACE = 'sheep.dailys.detail';
export const RELEASED_SHEEP_DAILYS_DETAIL_PATHNAME_PREFIX = SHEEP_DAILYS_DETAIL_PATHNAME_PREFIX;
export const RELEASED_EGG_DAILYS_INDEX_SURFACE = 'layer.eggDailys.index';
export const RELEASED_EGG_DAILYS_INDEX_PATHNAME = EGG_DAILYS_INDEX_PATHNAME;
export const RELEASED_EGG_DAILYS_DETAIL_SURFACE = 'layer.eggDailys.detail';
export const RELEASED_EGG_DAILYS_DETAIL_PATHNAME_PREFIX = EGG_DAILYS_DETAIL_PATHNAME_PREFIX;
export const RELEASED_PIG_DAILYS_INDEX_SURFACE = 'pig.dailys.index';
export const RELEASED_PIG_DAILYS_INDEX_PATHNAME = PIG_DAILYS_INDEX_PATHNAME;
export const RELEASED_PIG_DAILYS_DETAIL_SURFACE = 'pig.dailys.detail';
export const RELEASED_PIG_DAILYS_DETAIL_PATHNAME_PREFIX = PIG_DAILYS_DETAIL_PATHNAME_PREFIX;
export const RELEASED_PIG_BATCHES_INDEX_SURFACE = 'pig.batches.index';
export const RELEASED_PIG_BATCHES_INDEX_PATHNAME = PIG_BATCHES_INDEX_PATHNAME;
export const RELEASED_PIG_BATCHES_DETAIL_SURFACE = 'pig.batches.detail';
export const RELEASED_PIG_BATCHES_DETAIL_PATHNAME_PREFIX = PIG_BATCHES_DETAIL_PATHNAME_PREFIX;
export const RELEASED_PIG_FARROWING_SURFACE = 'view.farrowing';
export const RELEASED_PIG_FARROWING_PATHNAME = '/pig/farrowing';
export const RELEASED_BROILER_DAILYS_INDEX_SURFACE = 'broiler.dailys.index';
export const RELEASED_BROILER_DAILYS_INDEX_PATHNAME = BROILER_DAILYS_INDEX_PATHNAME;
export const RELEASED_BROILER_DAILYS_DETAIL_SURFACE = 'broiler.dailys.detail';
export const RELEASED_BROILER_DAILYS_DETAIL_PATHNAME_PREFIX = BROILER_DAILYS_DETAIL_PATHNAME_PREFIX;
export const RELEASED_TASKS_CENTER_SURFACE = 'tasks.center';
export const RELEASED_TASKS_CENTER_PATHNAME = TASKS_CENTER_PATHNAME;
export const RELEASED_TASKS_CENTER_TRAILING_PATHNAME = TASKS_CENTER_TRAILING_PATHNAME;
export const RELEASED_TASKS_TODO_PATHNAME = TASKS_TODO_PATHNAME;
export const RELEASED_TASKS_TODO_TRAILING_PATHNAME = TASKS_TODO_TRAILING_PATHNAME;
export const RELEASED_TASKS_TODO_DETAIL_SURFACE = 'tasks.todo.detail';
export const RELEASED_TASKS_TODO_DETAIL_PATHNAME_PREFIX = TASKS_TODO_DETAIL_PATHNAME_PREFIX;
export const RELEASED_TASKS_INSTANCE_DETAIL_SURFACE = 'tasks.instance.detail';
export const RELEASED_TASKS_INSTANCE_DETAIL_PATHNAME_PREFIX = TASKS_INSTANCE_DETAIL_PATHNAME_PREFIX;
export const RELEASED_ANIMAL_HISTORY_SURFACE = 'view.animalHistory';
export const RELEASED_ANIMAL_HISTORY_PATHNAME = '/animals-on-farm';
export const RELEASED_WEIGHINS_WEBFORM_SURFACE = 'view.weighins';
export const RELEASED_WEIGHINS_WEBFORM_PATHNAME = '/weighins';
export const RELEASED_WEIGHIN_SESSION_RECORD_SURFACE = 'view.weighinsessions';
export const RELEASED_WEIGHIN_SESSION_RECORD_PATHNAME_PREFIX = WEIGHIN_SESSION_RECORD_PATHNAME_PREFIX;
export const RELEASED_BROILER_TIMELINE_SURFACE = 'view.timeline';
export const RELEASED_BROILER_TIMELINE_PATHNAME = '/broiler/timeline';
export const RELEASED_BROILER_FEED_SURFACE = 'view.feed';
export const RELEASED_BROILER_FEED_PATHNAME = '/broiler/feed';
export const RELEASED_PIG_FEED_SURFACE = 'view.pigs';
export const RELEASED_PIG_FEED_PATHNAME = '/pig/feed';
export const RELEASED_BROILER_BATCH_FORM_FULLSCREEN_SURFACE = 'broiler.batchForm.fullscreen';

type ReleasedTarget = Readonly<{
  view: string;
  surface: string;
  pathname: string;
}>;

const CATTLE_BREEDING_TARGET: ReleasedTarget = Object.freeze({
  view: 'cattlebreeding',
  surface: RELEASED_CATTLE_BREEDING_SURFACE,
  pathname: RELEASED_CATTLE_BREEDING_PATHNAME,
});
// CattleDailysHub does not interpret search/hash; the current browser location
// may carry either without changing this canonical hub claim. A trailing slash
// or record suffix stays out of the raw release pair below.
const CATTLE_DAILYS_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'cattledailys',
  surface: RELEASED_CATTLE_DAILYS_INDEX_SURFACE,
  pathname: RELEASED_CATTLE_DAILYS_INDEX_PATHNAME,
});
const MY_SUBMISSIONS_TARGET: ReleasedTarget = Object.freeze({
  view: 'mySubmissions',
  surface: RELEASED_MY_SUBMISSIONS_SURFACE,
  pathname: RELEASED_MY_SUBMISSIONS_PATHNAME,
});
const FUEL_SUPPLY_TARGET: ReleasedTarget = Object.freeze({
  view: 'fuelSupply',
  surface: RELEASED_FUEL_SUPPLY_SURFACE,
  pathname: RELEASED_FUEL_SUPPLY_PATHNAME,
});
const EQUIPMENT_FUELING_SUPPLY_TARGET: ReleasedTarget = Object.freeze({
  view: 'fuelingHub',
  surface: RELEASED_EQUIPMENT_FUELING_SUPPLY_SURFACE,
  pathname: RELEASED_EQUIPMENT_FUELING_SUPPLY_PATHNAME,
});
const EQUIPMENT_FUELING_HUB_TARGET: ReleasedTarget = Object.freeze({
  view: 'fuelingHub',
  surface: RELEASED_EQUIPMENT_FUELING_HUB_SURFACE,
  pathname: RELEASED_EQUIPMENT_FUELING_HUB_PATHNAME,
});
const ACTIVITY_TARGET: ReleasedTarget = Object.freeze({
  view: 'activity',
  surface: RELEASED_ACTIVITY_SURFACE,
  pathname: RELEASED_ACTIVITY_PATHNAME,
});
// Projects: the exact /projects pathname. The open job is ?job= search state
// inside the same surface (N14), never a second pathname.
const PROJECTS_TARGET: ReleasedTarget = Object.freeze({
  view: 'projects',
  surface: RELEASED_PROJECTS_SURFACE,
  pathname: RELEASED_PROJECTS_PATHNAME,
});
const ADD_FEED_TARGET: ReleasedTarget = Object.freeze({
  view: 'addfeed',
  surface: RELEASED_ADD_FEED_SURFACE,
  pathname: RELEASED_ADD_FEED_PATHNAME,
});
const NEWSLETTER_ADMIN_TARGET: ReleasedTarget = Object.freeze({
  view: 'newsletterAdmin',
  surface: RELEASED_NEWSLETTER_ADMIN_SURFACE,
  pathname: RELEASED_NEWSLETTER_ADMIN_PATHNAME,
});
// The three /admin* rows are separate EXACT route entries, so none of them can
// own another's pathname: `routePatternOwns` matches on exact equality or a
// `/*` pattern, and `claimedExactlyByAnotherRow` refuses the pair a second
// time. ClientErrorsView reads neither `location.search` nor `location.hash`,
// so a carrier at this exact path is the same render.
const CLIENT_ERRORS_TARGET: ReleasedTarget = Object.freeze({
  view: 'clientErrors',
  surface: RELEASED_CLIENT_ERRORS_SURFACE,
  pathname: RELEASED_CLIENT_ERRORS_PATHNAME,
});
const WEBFORMS_ADMIN_TARGET: ReleasedTarget = Object.freeze({
  view: 'webforms',
  surface: RELEASED_WEBFORMS_ADMIN_SURFACE,
  pathname: RELEASED_WEBFORMS_ADMIN_PATHNAME,
});
// WEBFORMS_ROUTES dispatches ahead of every App table and ahead of the
// full-screen broiler form, and `/webform-pigs` is an exact row inside it, so
// nothing upstream can reclassify this pair. PigDailysWebform reads no
// search/hash state either.
const PIG_DAILYS_WEBFORM_TARGET: ReleasedTarget = Object.freeze({
  view: 'webform',
  surface: RELEASED_PIG_DAILYS_WEBFORM_SURFACE,
  pathname: RELEASED_PIG_DAILYS_WEBFORM_PATHNAME,
});
// `/dailys/tasks` is an EXACT row in WEBFORMS_ROUTES, NESTED inside the
// `/dailys/*` splat that `view.webformhub` owns. That nesting is the whole
// reason this pair has to stay an equality on the RAW pathname: the hub's own
// resolver fence already refuses `/dailys/tasks` for the hub candidate, and
// this target is the other half — the Tasks pair is a claim only at the exact
// Tasks pathname, never at `/dailys` or at any sub-form path the hub serves.
// TasksWebform reads neither `location.search` nor `location.hash`; its only
// location coupling is the route that mounts it, so a carrier at this exact
// path is the same render.
const TASKS_WEBFORM_TARGET: ReleasedTarget = Object.freeze({
  view: 'tasksWebform',
  surface: RELEASED_TASKS_WEBFORM_SURFACE,
  pathname: RELEASED_TASKS_WEBFORM_PATHNAME,
});
// `/pig/breeding` is an EXACT row in PIG_ROUTES — no `/*`, and no other row in
// any of the seven tables owns a path beneath it — so `routePatternOwns` pairs
// it with `breeding` on strict equality and the router can never hand this
// surface a descendant. Note the deliberate contrast with its sibling
// `/pig/sows/*` below: same domain, same file neighbourhood, opposite row shape,
// and therefore opposite target machinery. BreedingView reads no search or hash
// state; its own location coupling is `useUI().setView`, not the URL.
const PIG_BREEDING_TARGET: ReleasedTarget = Object.freeze({
  view: 'breeding',
  surface: RELEASED_PIG_BREEDING_SURFACE,
  pathname: RELEASED_PIG_BREEDING_PATHNAME,
});
// `{view: 'timeline', paths: ['/broiler/timeline']}` is an EXACT row in
// BROILER_ROUTES — one path, no `/*`, and no VIEW_VARIANTS split — so this pair
// is a plain equality like Pig Breeding above, not a prefix. The Gantt reads no
// search or hash, so a browser location carrying either does not change this
// claim; the raw pathname must still be exactly the row.
const BROILER_TIMELINE_TARGET: ReleasedTarget = Object.freeze({
  view: 'timeline',
  surface: RELEASED_BROILER_TIMELINE_SURFACE,
  pathname: RELEASED_BROILER_TIMELINE_PATHNAME,
});
// `{view: 'feed', paths: ['/broiler/feed']}` is an exact BROILER_ROUTES row,
// with no variant split and no descendant route. Its release is therefore the
// same raw equality contract as the timeline and pig-feed peers: a query/hash
// carrier preserves the render, while every other raw pathname stays legacy.
const BROILER_FEED_TARGET: ReleasedTarget = Object.freeze({
  view: 'feed',
  surface: RELEASED_BROILER_FEED_SURFACE,
  pathname: RELEASED_BROILER_FEED_PATHNAME,
});
// `{view: 'pigs', paths: ['/pig/feed']}` is the other EXACT row released in
// this wave — one path, no `/*`, and `pigs` has no VIEW_VARIANTS split. The
// splat sibling in the SAME table, `{view: 'sows', paths: ['/pig/sows/*']}`,
// needed a resolver fence; this one needs only the raw equality below, which
// is what refuses every descendant shape.
const PIG_FEED_TARGET: ReleasedTarget = Object.freeze({
  view: 'pigs',
  surface: RELEASED_PIG_FEED_SURFACE,
  pathname: RELEASED_PIG_FEED_PATHNAME,
});
const CATTLE_LOG_TARGET: ReleasedTarget = Object.freeze({
  view: 'cattlelog',
  surface: RELEASED_CATTLE_LOG_SURFACE,
  pathname: RELEASED_CATTLE_LOG_PATHNAME,
});
// `/pasture-map` is an EXACT row in APP_ROUTES_POST_FORM — no `/*`, and no
// other row in any of the seven tables owns a path beneath it — so
// `routePatternOwns` pairs it with `pastureMap` on strict equality and the
// router can never hand this surface a descendant. The raw pair below is what
// mints the capability, and it stays equality on the RAW browser pathname:
// PastureMapView reads neither `location.search` nor `location.hash`, so a
// carrier at this exact path is the same render, while a trailing slash or a
// descendant is a different raw pathname and never becomes a claim here.
const PASTURE_MAP_TARGET: ReleasedTarget = Object.freeze({
  view: 'pastureMap',
  surface: RELEASED_PASTURE_MAP_SURFACE,
  pathname: RELEASED_PASTURE_MAP_PATHNAME,
});
const PROCESSING_TARGET: ReleasedTarget = Object.freeze({
  view: 'processing',
  surface: RELEASED_PROCESSING_SURFACE,
  pathname: RELEASED_PROCESSING_PATHNAME,
});
const PRODUCTION_TARGET: ReleasedTarget = Object.freeze({
  view: 'production',
  surface: RELEASED_PRODUCTION_SURFACE,
  pathname: RELEASED_PRODUCTION_PATHNAME,
});
const CATTLE_HOME_TARGET: ReleasedTarget = Object.freeze({
  view: 'cattleHome',
  surface: RELEASED_CATTLE_HOME_SURFACE,
  pathname: RELEASED_CATTLE_HOME_PATHNAME,
});
const SHEEP_HOME_TARGET: ReleasedTarget = Object.freeze({
  view: 'sheepHome',
  surface: RELEASED_SHEEP_HOME_SURFACE,
  pathname: RELEASED_SHEEP_HOME_PATHNAME,
});
// The three remaining program dashboards are EXACT route rows — `/broiler`,
// `/layer` and `/pig` each carry no `/*` — so `routePatternOwns` pairs each of
// them with its own view on strict equality alone and no descendant can be
// classified here. Their route spaces are still largely legacy, which is
// exactly why the raw pair below has to stay exact: `exactTargetForPair`
// compares the RAW browser pathname, so a `/broiler/timeline` frame that has
// already flipped `view` to `broilerHome` mints no capability and keeps
// rendering what `view === 'broilerHome'` already rendered.
const BROILER_HOME_TARGET: ReleasedTarget = Object.freeze({
  view: 'broilerHome',
  surface: RELEASED_BROILER_HOME_SURFACE,
  pathname: RELEASED_BROILER_HOME_PATHNAME,
});
const LAYERS_HOME_TARGET: ReleasedTarget = Object.freeze({
  view: 'layersHome',
  surface: RELEASED_LAYERS_HOME_SURFACE,
  pathname: RELEASED_LAYERS_HOME_PATHNAME,
});
// `/layer/groups` is an EXACT row in LAYER_ROUTES. Its presenter reads neither
// location.search nor location.hash, so those carriers remain the same audited
// render; a trailing slash or a descendant never mints this raw capability.
const LAYER_GROUPS_TARGET: ReleasedTarget = Object.freeze({
  view: 'layers',
  surface: RELEASED_LAYER_GROUPS_SURFACE,
  pathname: RELEASED_LAYER_GROUPS_PATHNAME,
});
const PIGS_HOME_TARGET: ReleasedTarget = Object.freeze({
  view: 'pigsHome',
  surface: RELEASED_PIGS_HOME_SURFACE,
  pathname: RELEASED_PIGS_HOME_PATHNAME,
});
const WEATHER_TARGET: ReleasedTarget = Object.freeze({
  view: 'weather',
  surface: RELEASED_WEATHER_SURFACE,
  pathname: RELEASED_WEATHER_PATHNAME,
});
const CATTLE_FORECAST_TARGET: ReleasedTarget = Object.freeze({
  view: 'cattleforecast',
  surface: RELEASED_CATTLE_FORECAST_SURFACE,
  pathname: RELEASED_CATTLE_FORECAST_PATHNAME,
});
// CattleBatchesHub does not interpret search/hash — its toolbar, sort, and
// saved-view state are component/persisted local state, never URL state — so
// the current browser location may carry either without changing this
// canonical hub claim. The trailing slash and every record/forecast suffix
// stay out of the raw release pair below.
const CATTLE_BATCHES_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'cattlebatches',
  surface: RELEASED_CATTLE_BATCHES_INDEX_SURFACE,
  pathname: RELEASED_CATTLE_BATCHES_INDEX_PATHNAME,
});
// BroilerListHub's filters and saved views are component/persisted state, not
// URL state. The bare hub can therefore accept a presentation query or hash;
// its record sibling cannot because BroilerBatchPage consumes location.hash.
const BROILER_BATCHES_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'list',
  surface: RELEASED_BROILER_BATCHES_INDEX_SURFACE,
  pathname: RELEASED_BROILER_BATCHES_INDEX_PATHNAME,
});
const LAYER_BATCHES_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'layerbatches',
  surface: RELEASED_LAYER_BATCHES_INDEX_SURFACE,
  pathname: RELEASED_LAYER_BATCHES_INDEX_PATHNAME,
});
// LayerDailysHub calls no location hook at all, so search and hash cannot
// change this canonical hub claim. The splat base `/layer/dailys/` and every
// record descendant stay out of this raw pair.
const LAYER_DAILYS_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'layerdailys',
  surface: RELEASED_LAYER_DAILYS_INDEX_SURFACE,
  pathname: RELEASED_LAYER_DAILYS_INDEX_PATHNAME,
});
// The other four daily hubs, on the same terms as the two above. None of them
// calls a location hook — each router reads `location.pathname` and hands its
// hub the row set — so search and hash cannot change these canonical claims.
// Each splat base (`/x/dailys/`, `/layer/eggs/`) and every record descendant
// stays out of these raw pairs.
const EGG_DAILYS_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'eggdailys',
  surface: RELEASED_EGG_DAILYS_INDEX_SURFACE,
  pathname: RELEASED_EGG_DAILYS_INDEX_PATHNAME,
});
const PIG_DAILYS_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'pigdailys',
  surface: RELEASED_PIG_DAILYS_INDEX_SURFACE,
  pathname: RELEASED_PIG_DAILYS_INDEX_PATHNAME,
});
const PIG_BATCHES_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'pigbatches',
  surface: RELEASED_PIG_BATCHES_INDEX_SURFACE,
  pathname: RELEASED_PIG_BATCHES_INDEX_PATHNAME,
});
const SHEEP_DAILYS_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'sheepdailys',
  surface: RELEASED_SHEEP_DAILYS_INDEX_SURFACE,
  pathname: RELEASED_SHEEP_DAILYS_INDEX_PATHNAME,
});
const BROILER_DAILYS_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'broilerdailys',
  surface: RELEASED_BROILER_DAILYS_INDEX_SURFACE,
  pathname: RELEASED_BROILER_DAILYS_INDEX_PATHNAME,
});
const EQUIPMENT_FLEET_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'equipmentHome',
  surface: RELEASED_EQUIPMENT_FLEET_INDEX_SURFACE,
  pathname: RELEASED_EQUIPMENT_FLEET_INDEX_PATHNAME,
});
const EQUIPMENT_FLEET_FUEL_LOG_TARGET: ReleasedTarget = Object.freeze({
  view: 'equipmentHome',
  surface: RELEASED_EQUIPMENT_FLEET_INDEX_SURFACE,
  pathname: RELEASED_EQUIPMENT_FLEET_FUEL_LOG_PATHNAME,
});
// Home has two independent render surfaces at one raw pathname. The resolver,
// not an ad-hoc role check here, decides which one its current auth state names.
// Keeping both targets explicit lets the boundary prove that the resulting
// surface agrees with the candidate before it mints either capability.
const STANDARD_HOME_TARGET: ReleasedTarget = Object.freeze({
  view: 'home',
  surface: RELEASED_STANDARD_HOME_SURFACE,
  pathname: RELEASED_STANDARD_HOME_PATHNAME,
});
const LIGHT_HOME_TARGET: ReleasedTarget = Object.freeze({
  view: 'home',
  surface: RELEASED_LIGHT_HOME_SURFACE,
  pathname: RELEASED_LIGHT_HOME_PATHNAME,
});

// Broiler and Pig weigh-ins mount ONE shared list component whose species prop
// is the only thing separating them. They are therefore two independent
// targets, never one parameterized entry: a single row keyed on a species
// segment would let either raw route mint the other's claim, and the render
// boundary re-checks the exact surface/pathname pair rather than a family.
const BROILER_WEIGHINS_TARGET: ReleasedTarget = Object.freeze({
  view: 'broilerweighins',
  surface: RELEASED_BROILER_WEIGHINS_SURFACE,
  pathname: RELEASED_BROILER_WEIGHINS_PATHNAME,
});
const PIG_WEIGHINS_TARGET: ReleasedTarget = Object.freeze({
  view: 'pigweighins',
  surface: RELEASED_PIG_WEIGHINS_SURFACE,
  pathname: RELEASED_PIG_WEIGHINS_PATHNAME,
});

// Both weigh-in lists own one exact route apiece. CattleRoutes and SheepRoutes
// keep these rows splat-free, so there is no record space to fence and no
// dedicated raw-path matcher module: an exact pathname comparison is the whole
// contract. They stay two separate targets rather than one parameterized pair
// because their programs — and therefore their access denials — differ.
const CATTLE_WEIGHINS_TARGET: ReleasedTarget = Object.freeze({
  view: 'cattleweighins',
  surface: RELEASED_CATTLE_WEIGHINS_SURFACE,
  pathname: RELEASED_CATTLE_WEIGHINS_PATHNAME,
});
const SHEEP_WEIGHINS_TARGET: ReleasedTarget = Object.freeze({
  view: 'sheepweighins',
  surface: RELEASED_SHEEP_WEIGHINS_SURFACE,
  pathname: RELEASED_SHEEP_WEIGHINS_PATHNAME,
});
// AnimalHistoryPage reads neither `location.search` nor `location.hash` — its
// only navigation coupling is `useUI().setView` — so the current browser
// location may carry either without changing this exact claim. `/animals-on-farm`
// is an EXACT row in APP_ROUTES_PRE_FORM, so no descendant pathname can reach
// this surface: `routePatternOwns` matches a non-splat pattern only on equality.
const ANIMAL_HISTORY_TARGET: ReleasedTarget = Object.freeze({
  view: 'animalHistory',
  surface: RELEASED_ANIMAL_HISTORY_SURFACE,
  pathname: RELEASED_ANIMAL_HISTORY_PATHNAME,
});
// WeighInsWebform reads neither `location.search` nor `location.hash`; it WRITES
// `window.location.hash = '#webforms'` on its two "back to Daily Reports"
// controls and immediately reloads, and `applyHashCompat` rewrites that to
// `/dailys` inside `initRuntime()` BEFORE the first render — so no hash this
// surface produces ever reaches a released claim. `/weighins` is an EXACT row in
// WEBFORMS_ROUTES, so it owns no descendants either.
const WEIGHINS_WEBFORM_TARGET: ReleasedTarget = Object.freeze({
  view: 'weighins',
  surface: RELEASED_WEIGHINS_WEBFORM_SURFACE,
  pathname: RELEASED_WEIGHINS_WEBFORM_PATHNAME,
});

// CattleHerdsHub reads no search or hash; only the router's pathname selects
// between the two halves of this partition. The wildcard base and every record
// suffix stay out of this hub pair.
const CATTLE_HERDS_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'cattleherds',
  surface: RELEASED_CATTLE_HERDS_INDEX_SURFACE,
  pathname: RELEASED_CATTLE_HERDS_INDEX_PATHNAME,
});

// Neither sheep hub interprets search or hash; the wildcard router base, a
// trailing slash, and every record descendant stay out of these raw pairs.
const SHEEP_BATCHES_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'sheepbatches',
  surface: RELEASED_SHEEP_BATCHES_INDEX_SURFACE,
  pathname: RELEASED_SHEEP_BATCHES_INDEX_PATHNAME,
});
const SHEEP_FLOCKS_INDEX_TARGET: ReleasedTarget = Object.freeze({
  view: 'sheepflocks',
  surface: RELEASED_SHEEP_FLOCKS_INDEX_SURFACE,
  pathname: RELEASED_SHEEP_FLOCKS_INDEX_PATHNAME,
});

// TasksRouter reaches one surface at four raw forms, so `tasks.center` needs a
// fence rather than a single-pathname pair, exactly like the Fleet index. Task
// and To Do record paths are separate unreleased surfaces and are excluded by
// the shared route contract, not by this table.
const TASKS_CENTER_TARGET: ReleasedTarget = Object.freeze({
  view: 'tasks',
  surface: RELEASED_TASKS_CENTER_SURFACE,
  pathname: RELEASED_TASKS_CENTER_PATHNAME,
});
const TASKS_CENTER_TRAILING_TARGET: ReleasedTarget = Object.freeze({
  view: 'tasks',
  surface: RELEASED_TASKS_CENTER_SURFACE,
  pathname: RELEASED_TASKS_CENTER_TRAILING_PATHNAME,
});
const TASKS_TODO_TARGET: ReleasedTarget = Object.freeze({
  view: 'tasks',
  surface: RELEASED_TASKS_CENTER_SURFACE,
  pathname: RELEASED_TASKS_TODO_PATHNAME,
});
const TASKS_TODO_TRAILING_TARGET: ReleasedTarget = Object.freeze({
  view: 'tasks',
  surface: RELEASED_TASKS_CENTER_SURFACE,
  pathname: RELEASED_TASKS_TODO_TRAILING_PATHNAME,
});

type ReleasedResolution = Extract<AuthenticatedSurfaceResolution, {mode: typeof AUTHENTICATED_SURFACE_MODE.RELEASED}>;

export type ReleasedSurfaceClaim = Readonly<{
  kind: 'released-surface';
  userId: string;
  sessionEpoch: number;
  runId: number;
  resolution: ReleasedResolution;
}>;

export type ReleasedSurfaceBoundaryInput = Readonly<{
  lease: LegacyBootLease | null | undefined;
  authState: unknown;
  view: AuthenticatedSurfaceResolverInput['view'];
  location: AuthenticatedSurfaceResolverInput['location'];
  showForm?: AuthenticatedSurfaceResolverInput['showForm'];
  editId?: AuthenticatedSurfaceResolverInput['editId'];
}>;

/**
 * Once the all-data loader is retired, an authenticated route must make an
 * explicit choice instead of treating every non-release as permission to boot
 * the old application shell.  The distinction is deliberately small:
 *
 * - `released`: App may mint/render the reviewed independent surface.
 * - `sync-pending`: URL -> view synchronization owns the next render.
 * - `redirect`: one narrowly reviewed compatibility spelling can canonicalize.
 * - `terminal`: every other malformed, fenced, denied, or unowned raw route
 *   gets a bounded recovery screen.  It never reaches legacy dispatch.
 */
export type AuthenticatedRouteDisposition =
  | Readonly<{kind: 'released'}>
  | Readonly<{kind: 'sync-pending'}>
  | Readonly<{kind: 'redirect'; pathname: string}>
  | Readonly<{kind: 'terminal'; reason: string}>;

function isPositiveSafeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function isCurrentLease(lease: LegacyBootLease | null | undefined): lease is LegacyBootLease {
  if (!lease || typeof lease !== 'object') return false;
  if (typeof lease.userId !== 'string' || lease.userId.trim() === '') return false;
  if (!isPositiveSafeInteger(lease.sessionEpoch) || !isPositiveSafeInteger(lease.runId)) return false;
  if (typeof lease.isCurrent !== 'function') return false;
  try {
    return lease.isCurrent();
  } catch {
    return false;
  }
}

function pathnameFrom(location: unknown): string | null {
  if (typeof location === 'string') return location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return null;
  const pathname = (location as {pathname?: unknown}).pathname;
  return typeof pathname === 'string' ? pathname : null;
}

/**
 * The full-screen form is an App host surface, not a route-table row. This is
 * the exact raw pathname predicate App has always used for it: the whole
 * broiler space (including the bare batches hub) except an embedded record
 * path. Search/hash do not participate because BatchForm does not read either.
 */
export function isReleasedBroilerBatchFormFullscreenHostPathname(pathname: unknown): pathname is string {
  return (
    typeof pathname === 'string' &&
    (pathname === '/broiler' || pathname.startsWith('/broiler/')) &&
    !pathname.startsWith('/broiler/batches/')
  );
}

// Kept as a release-boundary export so capability consumers do not need to
// learn the neutral route-contract module's internal name.
export const isReleasedCattleBatchesDetailPathname = isCanonicalCattleBatchesDetailPathname;
export const isReleasedCattleBatchesIndexPathname = isCanonicalCattleBatchesIndexPathname;
export const isReleasedCattleForecastBatchDetailPathname = isCanonicalCattleForecastBatchDetailPathname;
export const isReleasedEquipmentFleetIndexPathname = isCanonicalFleetIndexPathname;
export const isReleasedEquipmentFleetDetailPathname = isCanonicalFleetDetailPathname;
export const isReleasedEquipmentFuelingEntryPathname = isCanonicalFleetFuelingEntryPathname;
export const isReleasedEquipmentChecklistEntryPathname = isCanonicalFleetChecklistEntryPathname;
export const isReleasedEquipmentFuelingFormPathname = isCanonicalEquipmentFuelingFormPathname;
// Kept as the release-boundary export so existing capability consumers do not
// learn the neutral route-contract module's internal name.
export const isReleasedCattleDailysDetailPathname = isCanonicalCattleDailysDetailPathname;
export const isReleasedTasksTodoDetailPathname = isCanonicalTasksTodoDetailPathname;
export const isReleasedTasksInstanceDetailPathname = isCanonicalTasksInstanceDetailPathname;
// Keep consumers on the release-boundary name while the neutral path module
// remains the sole source of truth for the hub's seven-path whitelist.
export const isReleasedWebformHubDailysPathname = isCanonicalWebformHubDailysPathname;

// `/dailys/*` is one surface but not one raw route. The presenter owns the
// selector and six named forms, while `/dailys/tasks` is an exact sibling with
// another view and every other suffix remains legacy. Delegate to the existing
// whitelist rather than rebuilding a splat matcher here, so a claim and the
// resolver fence cannot drift apart.
function webformHubDailysTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalWebformHubDailysLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'webformhub',
    surface: RELEASED_WEBFORM_HUB_SURFACE,
    pathname,
  });
}

// The two Tasks record targets are DISJOINT by construction, not by evaluation
// order: a `/tasks/todo/<id>` pathname leaves `todo/<id>` in the instance
// position, which carries a separator, and the bare segment `todo` is reserved
// besides. Each target admits the same canonical `#comment-<id>` anchor the
// record page scrolls to, but no search or arbitrary fragment. Neither claims
// `/tasks` or `/tasks/todo`, which TasksRouter renders as the separate Task
// Center surface.
function tasksTodoDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalTasksTodoDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'tasks',
    surface: RELEASED_TASKS_TODO_DETAIL_SURFACE,
    pathname,
  });
}

function tasksInstanceDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalTasksInstanceDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'tasks',
    surface: RELEASED_TASKS_INSTANCE_DETAIL_SURFACE,
    pathname,
  });
}

function tasksRecordTarget(location: unknown): ReleasedTarget | null {
  return tasksTodoDetailTarget(location) || tasksInstanceDetailTarget(location);
}

function cattleDailysDetailTarget(pathname: string | null): ReleasedTarget | null {
  if (!isReleasedCattleDailysDetailPathname(pathname)) return null;
  return Object.freeze({
    view: 'cattledailys',
    surface: RELEASED_CATTLE_DAILYS_DETAIL_SURFACE,
    pathname,
  });
}

export const isReleasedCattleHerdsIndexPathname = isCanonicalCattleHerdsIndexPathname;
export const isReleasedCattleHerdsDetailPathname = isCanonicalCattleHerdsDetailPathname;

function cattleHerdsTarget(location: unknown): ReleasedTarget | null {
  if (isCanonicalCattleHerdsIndexLocation(location)) return CATTLE_HERDS_INDEX_TARGET;
  if (!isCanonicalCattleHerdsDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'cattleherds',
    surface: RELEASED_CATTLE_HERDS_DETAIL_SURFACE,
    pathname,
  });
}

export const isReleasedSheepBatchesDetailPathname = isCanonicalSheepBatchesDetailPathname;
export const isReleasedSheepFlocksDetailPathname = isCanonicalSheepFlocksDetailPathname;

function sheepBatchesDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalSheepBatchesDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'sheepbatches',
    surface: RELEASED_SHEEP_BATCHES_DETAIL_SURFACE,
    pathname,
  });
}

function sheepFlocksDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalSheepFlocksDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'sheepflocks',
    surface: RELEASED_SHEEP_FLOCKS_DETAIL_SURFACE,
    pathname,
  });
}

export const isReleasedSheepDailysDetailPathname = isCanonicalSheepDailysDetailPathname;
export const isReleasedTasksCenterPathname = isCanonicalTasksCenterPathname;
export const isReleasedLayerDailysIndexPathname = isCanonicalLayerDailysIndexPathname;
export const isReleasedLayerDailysDetailPathname = isCanonicalLayerDailysDetailPathname;

// The two Layer Dailys targets are DISJOINT by construction, not by evaluation
// order: the index predicate is an EQUALITY against `/layer/dailys`, while the
// record predicate requires the strictly longer `/layer/dailys/` prefix plus a
// non-empty segment. No pathname can satisfy both, and neither claims the splat
// base `/layer/dailys/`, which LayerDailysRouter renders as the hub but which
// no reviewer admitted into either release.
function layerDailysIndexTarget(location: unknown): ReleasedTarget | null {
  return isCanonicalLayerDailysIndexLocation(location) ? LAYER_DAILYS_INDEX_TARGET : null;
}

function layerDailysDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalLayerDailysDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'layerdailys',
    surface: RELEASED_LAYER_DAILYS_DETAIL_SURFACE,
    pathname,
  });
}

function layerDailysTarget(location: unknown): ReleasedTarget | null {
  return layerDailysIndexTarget(location) || layerDailysDetailTarget(location);
}

export const isReleasedEggDailysIndexPathname = isCanonicalEggDailysIndexPathname;
export const isReleasedEggDailysDetailPathname = isCanonicalEggDailysDetailPathname;
export const isReleasedPigDailysIndexPathname = isCanonicalPigDailysIndexPathname;
export const isReleasedPigDailysDetailPathname = isCanonicalPigDailysDetailPathname;
export const isReleasedPigBatchesIndexPathname = isCanonicalPigBatchesIndexPathname;
export const isReleasedPigBatchesDetailPathname = isCanonicalPigBatchesDetailPathname;
export const isReleasedSheepDailysIndexPathname = isCanonicalSheepDailysIndexPathname;
export const isReleasedBroilerDailysIndexPathname = isCanonicalBroilerDailysIndexPathname;
export const isReleasedBroilerDailysDetailPathname = isCanonicalBroilerDailysDetailPathname;
export const isReleasedBroilerBatchesIndexPathname = isCanonicalBroilerBatchesIndexPathname;
export const isReleasedBroilerBatchesDetailPathname = isCanonicalBroilerBatchesDetailPathname;

// Each of the four pairs below partitions its own splat exactly the way the
// Layer Dailys pair does, and for the same structural reason rather than by
// evaluation order: the index predicate is an EQUALITY against the bare base,
// while the record predicate requires the strictly longer `<base>/` prefix plus
// a non-empty decoded-safe segment. No pathname can satisfy both, and neither
// claims the splat base (`/x/dailys/`), which each router really does render as
// its hub but which no reviewer admitted into either release.
function eggDailysIndexTarget(location: unknown): ReleasedTarget | null {
  return isCanonicalEggDailysIndexLocation(location) ? EGG_DAILYS_INDEX_TARGET : null;
}

function eggDailysDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalEggDailysDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'eggdailys',
    surface: RELEASED_EGG_DAILYS_DETAIL_SURFACE,
    pathname,
  });
}

function eggDailysTarget(location: unknown): ReleasedTarget | null {
  return eggDailysIndexTarget(location) || eggDailysDetailTarget(location);
}

function pigDailysIndexTarget(location: unknown): ReleasedTarget | null {
  return isCanonicalPigDailysIndexLocation(location) ? PIG_DAILYS_INDEX_TARGET : null;
}

function pigDailysDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalPigDailysDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'pigdailys',
    surface: RELEASED_PIG_DAILYS_DETAIL_SURFACE,
    pathname,
  });
}

function pigDailysTarget(location: unknown): ReleasedTarget | null {
  return pigDailysIndexTarget(location) || pigDailysDetailTarget(location);
}

function pigBatchesIndexTarget(location: unknown): ReleasedTarget | null {
  return isCanonicalPigBatchesIndexLocation(location) ? PIG_BATCHES_INDEX_TARGET : null;
}

function pigBatchesDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalPigBatchesDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'pigbatches',
    surface: RELEASED_PIG_BATCHES_DETAIL_SURFACE,
    pathname,
  });
}

function pigBatchesTarget(location: unknown): ReleasedTarget | null {
  return pigBatchesIndexTarget(location) || pigBatchesDetailTarget(location);
}

function broilerDailysIndexTarget(location: unknown): ReleasedTarget | null {
  return isCanonicalBroilerDailysIndexLocation(location) ? BROILER_DAILYS_INDEX_TARGET : null;
}

function broilerDailysDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalBroilerDailysDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'broilerdailys',
    surface: RELEASED_BROILER_DAILYS_DETAIL_SURFACE,
    pathname,
  });
}

function broilerDailysTarget(location: unknown): ReleasedTarget | null {
  return broilerDailysIndexTarget(location) || broilerDailysDetailTarget(location);
}

export const isReleasedLayerHousingsDetailPathname = isCanonicalLayerHousingsDetailPathname;

// The released half of the `layerbatches` view. It shares that view name with
// two surfaces that are NOT released targets — the `/layer/batches/<id>` record
// page and the wildcard hub fallback — so this target is keyed on the housings
// prefix, which neither of them can carry. It is disjoint from
// LAYER_BATCHES_INDEX_TARGET by construction: that one is an EQUALITY against
// `/layer/batches`, and this one requires the strictly different
// `/layer/housings/` prefix plus a non-empty segment.
function layerHousingsDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalLayerHousingsDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'layerbatches',
    surface: RELEASED_LAYER_HOUSINGS_DETAIL_SURFACE,
    pathname,
  });
}

export const isReleasedLayerBatchesDetailPathname = isCanonicalLayerBatchesDetailPathname;

export const isReleasedWeighInSessionRecordPathname = isCanonicalWeighInSessionRecordPathname;

export const isReleasedProcessingCutSheetPathname = isCanonicalCutSheetEditorPathname;

export const isReleasedEquipmentAdminRecordPathname = isCanonicalAdminEquipmentRecordPathname;

export const isReleasedPigFarrowingPathname = isCanonicalPigFarrowingPathname;

function pigFarrowingTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalPigFarrowingLocation(location)) return null;
  const pathname = pathnameFrom(location);
  return pathname ? Object.freeze({view: 'farrowing', surface: RELEASED_PIG_FARROWING_SURFACE, pathname}) : null;
}

export const isReleasedPigSowsPathname = isCanonicalPigSowsPathname;

// `/pig/sows/*` is the second `view.*` SPLAT space to become a released target,
// and like `/weigh-in-sessions/*` its whole space classifies as ONE surface —
// `sows` has no `VIEW_VARIANTS` entry, so `surfaceForRoutedView` answers
// `view.sows` for the list, for every record path, and for every view-led
// transition frame. So this target function IS the partition, and it delegates
// to the SAME predicate the resolver fence uses rather than restating it: the
// bare `/pig/sows` list, or `/pig/sows/<one decoded-safe segment>` for the
// record mode. There is no index/detail sibling pair to be disjoint from —
// SowsView is a `sub-mode` surface serving both from one component — which is
// exactly why one function returns the raw pathname instead of two functions
// returning two constants.
//
// Search and hash are ADMITTED here, unlike the weigh-in session record fence
// next door. That is not an oversight: nothing in the SowsView subtree reads
// `location.search` or `location.hash`, so a carrier is the same render, and
// pigSowsPath.ts records the derivation.
function pigSowsTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalPigSowsLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'sows',
    surface: RELEASED_PIG_SOWS_SURFACE,
    pathname,
  });
}

// The whole `/weigh-in-sessions/*` splat space classifies as ONE surface — the
// view has no `VIEW_VARIANTS` entry — so this target function IS the partition:
// exactly one decoded-safe segment with no search and, at most, the canonical
// `#comment-<id>` anchor the record page interprets. There is no index sibling
// to be disjoint from; the bare base, splat base, malformed suffixes, and
// arbitrary fragments all terminal-recover.
function weighInSessionRecordTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalWeighInSessionRecordLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'weighinsessions',
    surface: RELEASED_WEIGHIN_SESSION_RECORD_SURFACE,
    pathname,
  });
}

// The cut-sheet editor, built on the weigh-in session record target above
// rather than on the `/processing` exact pair next to it. Its route row is a
// SPLAT with no index sibling, and `processingCutSheet` has no `VIEW_VARIANTS`
// entry, so `surfaceForRoutedView` answers `view.processingCutSheet` for the
// whole `/processing/cut-sheet/*` space — the splat base, every malformed id,
// and every view-led transition frame alike. So this one function IS the
// partition, and it delegates to the route contract's own location predicate
// instead of restating the shape: exactly one decoded-safe segment, plus at
// most the two carried UI facts — `?tab=diagram` and/or `#usual` (N06/N14,
// Table B). The questionnaire's own chapter/pool/draft state stays out of the
// URL, and every other search or hash refuses, so each supported state has one
// canonical address and stable released ownership (see cutSheetRecordPaths.ts).
//
// It cannot collide with PROCESSING_TARGET. That one is an EQUALITY against
// `/processing`; this one requires the strictly longer `/processing/cut-sheet/`
// prefix plus a non-empty segment, which the exact hub pathname cannot carry.
// Their views differ too, so neither ordering in the two dispatchers below can
// let one capture the other's route.
//
// The raw pathname is returned rather than a constant for the same reason the
// weigh-in session record target returns one: the record id is part of the
// address, so there is no single pathname this surface could be frozen onto,
// and `releasedResolutionForTarget` compares the resolver's `routedPathname`
// against exactly this value before any claim is issued.
function processingCutSheetTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalCutSheetEditorLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'processingCutSheet',
    surface: RELEASED_PROCESSING_CUT_SHEET_SURFACE,
    pathname,
  });
}

// A40: the Admin equipment record page, the same shape as the cut-sheet target
// above. Its route row is a splat with no index sibling and no VIEW_VARIANTS
// entry, so this one function is the partition of `/admin/equipment/*`, and it
// delegates to the route contract's location predicate (one decoded-safe
// segment, no search, no hash). It cannot collide with the webforms target:
// that one is an EQUALITY against `/admin`, and this one needs the strictly
// longer `/admin/equipment/` prefix plus a non-empty segment. The raw pathname
// is returned because the slug is part of the address.
function equipmentAdminRecordTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalAdminEquipmentRecordLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'equipmentAdminRecord',
    surface: RELEASED_EQUIPMENT_ADMIN_RECORD_SURFACE,
    pathname,
  });
}

// The THIRD and last released target of the `layerbatches` view. All three are
// disjoint by construction: LAYER_BATCHES_INDEX_TARGET is an EQUALITY against
// `/layer/batches`, the housings target requires the `/layer/housings/` prefix
// plus a non-empty segment, and this one requires the strictly longer
// `/layer/batches/` prefix plus a non-empty segment — which the bare hub
// pathname cannot carry and the housings prefix cannot match. What is left on
// legacy under this view is the wildcard remainder the three fences refuse.
function layerBatchesDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalLayerBatchesDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'layerbatches',
    surface: RELEASED_LAYER_BATCHES_DETAIL_SURFACE,
    pathname,
  });
}

function fleetIndexTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalFleetIndexLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (pathname === RELEASED_EQUIPMENT_FLEET_INDEX_PATHNAME) return EQUIPMENT_FLEET_INDEX_TARGET;
  if (pathname === RELEASED_EQUIPMENT_FLEET_FUEL_LOG_PATHNAME) return EQUIPMENT_FLEET_FUEL_LOG_TARGET;
  return null;
}

// The index release owns the two exact canonical pathnames; the detail release
// owns exactly one unreserved record segment. `RESERVED_FLEET_DETAIL_SEGMENTS`
// keeps `fuel-log`, `materials`, `fueling`, and `checklist` out of the record
// form, so the two predicates are disjoint and their evaluation order cannot
// let one release capture the other's route.
function fleetDetailTarget(pathname: string | null): ReleasedTarget | null {
  if (!isReleasedEquipmentFleetDetailPathname(pathname)) return null;
  return Object.freeze({
    view: 'equipmentHome',
    surface: RELEASED_EQUIPMENT_FLEET_DETAIL_SURFACE,
    pathname,
  });
}

// The two single-entry namespaces. Their predicates require a segment with no
// `/` in it under a two-segment prefix, so neither can satisfy the detail
// predicate (whose remainder would contain a `/`) nor each other's (distinct
// literal prefixes). Evaluation order below is therefore documentation of
// EquipmentHome's own dispatch, not a tie-break: no pathname reaches two.
function fleetFuelingEntryTarget(pathname: string | null): ReleasedTarget | null {
  if (!isReleasedEquipmentFuelingEntryPathname(pathname)) return null;
  return Object.freeze({
    view: 'equipmentHome',
    surface: RELEASED_EQUIPMENT_FUELING_ENTRY_SURFACE,
    pathname,
  });
}

function fleetChecklistEntryTarget(pathname: string | null): ReleasedTarget | null {
  if (!isReleasedEquipmentChecklistEntryPathname(pathname)) return null;
  return Object.freeze({
    view: 'equipmentHome',
    surface: RELEASED_EQUIPMENT_CHECKLIST_ENTRY_SURFACE,
    pathname,
  });
}

function equipmentFuelingFormTarget(pathname: string | null): ReleasedTarget | null {
  if (!isReleasedEquipmentFuelingFormPathname(pathname)) return null;
  return Object.freeze({
    view: 'fuelingHub',
    surface: RELEASED_EQUIPMENT_FUELING_FORM_SURFACE,
    pathname,
  });
}

function cattleForecastBatchDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalCattleForecastBatchDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'cattlebatches',
    surface: RELEASED_CATTLE_FORECAST_BATCH_DETAIL_SURFACE,
    pathname,
  });
}

// This router was asymmetric for one wave: only the exact decoded-safe,
// queryless record pathname was a target, while the hub beneath the same view
// name took whatever boot mode its own ledger membership gave it. Both halves
// are targets now, and they are disjoint by construction exactly as the Layer
// Dailys pair is — an EQUALITY against `/sheep/dailys` on one side, the
// strictly longer `/sheep/dailys/` prefix plus a non-empty segment on the
// other. The splat base belongs to neither.
function sheepDailysIndexTarget(location: unknown): ReleasedTarget | null {
  return isCanonicalSheepDailysIndexLocation(location) ? SHEEP_DAILYS_INDEX_TARGET : null;
}

function sheepDailysDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalSheepDailysDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'sheepdailys',
    surface: RELEASED_SHEEP_DAILYS_DETAIL_SURFACE,
    pathname,
  });
}

function sheepDailysTarget(location: unknown): ReleasedTarget | null {
  return sheepDailysIndexTarget(location) || sheepDailysDetailTarget(location);
}

function tasksCenterTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalTasksCenterLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (pathname === RELEASED_TASKS_CENTER_PATHNAME) return TASKS_CENTER_TARGET;
  if (pathname === RELEASED_TASKS_CENTER_TRAILING_PATHNAME) return TASKS_CENTER_TRAILING_TARGET;
  if (pathname === RELEASED_TASKS_TODO_PATHNAME) return TASKS_TODO_TARGET;
  if (pathname === RELEASED_TASKS_TODO_TRAILING_PATHNAME) return TASKS_TODO_TRAILING_TARGET;
  return null;
}

function cattleBatchesDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalCattleBatchesDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'cattlebatches',
    surface: RELEASED_CATTLE_BATCHES_DETAIL_SURFACE,
    pathname,
  });
}

// The three `/cattle/batches*` releases partition the space by pathname shape
// alone: the index is exactly `/cattle/batches`, while both siblings require
// the `/cattle/batches/` prefix and therefore a strictly longer pathname. No
// evaluation order here can let one of them capture another's route.
function cattleBatchesIndexTarget(location: unknown): ReleasedTarget | null {
  return isCanonicalCattleBatchesIndexLocation(location) ? CATTLE_BATCHES_INDEX_TARGET : null;
}

function broilerBatchesDetailTarget(location: unknown): ReleasedTarget | null {
  if (!isCanonicalBroilerBatchesDetailLocation(location)) return null;
  const pathname = pathnameFrom(location);
  if (!pathname) return null;
  return Object.freeze({
    view: 'list',
    surface: RELEASED_BROILER_BATCHES_DETAIL_SURFACE,
    pathname,
  });
}

function broilerBatchesIndexTarget(location: unknown): ReleasedTarget | null {
  return isCanonicalBroilerBatchesIndexLocation(location) ? BROILER_BATCHES_INDEX_TARGET : null;
}

// Unlike every table target below, the full-screen form is selected from
// `showForm` plus the physical pathname. Keep it out of exactTargetForPair and
// targetForPath: it is a direct capability, never a URL/view sync target. The
// resolver remains the final authority for webform and pre-form precedence.
function broilerBatchFormFullscreenTarget(input: ReleasedSurfaceBoundaryInput): ReleasedTarget | null {
  const pathname = pathnameFrom(input?.location);
  const hasEditId = typeof input?.editId === 'string' ? input.editId.trim().length > 0 : Boolean(input?.editId);
  const isTimelineCreate = pathname === RELEASED_BROILER_TIMELINE_PATHNAME && !hasEditId;
  if (
    !input?.showForm ||
    typeof input.view !== 'string' ||
    !isReleasedBroilerBatchFormFullscreenHostPathname(pathname) ||
    isTimelineCreate
  ) {
    return null;
  }
  return Object.freeze({
    view: input.view,
    surface: RELEASED_BROILER_BATCH_FORM_FULLSCREEN_SURFACE,
    pathname,
  });
}
/**
 * The generic authenticated-surface resolver deliberately supports a one-render
 * view-led navigation frame by canonicalizing an old pathname. That behavior
 * is correct for the historical App dispatcher, but it is too permissive for
 * independent releases: a capability may exist only once both the raw browser
 * route and the view state identify one audited surface.
 */
function exactTargetForPair(
  view: unknown,
  pathname: string | null,
  location: unknown = pathname,
): ReleasedTarget | null {
  if (view === 'equipmentHome') {
    const fleetTarget = fleetIndexTarget(location);
    if (fleetTarget) return fleetTarget;
    // Mirrors EquipmentHome's own dispatch: the two entry namespaces are
    // matched ahead of the generic record branch.
    const fuelingEntry = fleetFuelingEntryTarget(pathname);
    if (fuelingEntry) return fuelingEntry;
    const checklistEntry = fleetChecklistEntryTarget(pathname);
    if (checklistEntry) return checklistEntry;
    const fleetRecordTarget = fleetDetailTarget(pathname);
    if (fleetRecordTarget) return fleetRecordTarget;
  }
  if (view === 'tasks') {
    const centerTarget = tasksCenterTarget(location);
    if (centerTarget) return centerTarget;
  }
  if (view === 'cattlebatches') {
    const forecastTarget = cattleForecastBatchDetailTarget(location);
    if (forecastTarget) return forecastTarget;
    const detailTarget = cattleBatchesDetailTarget(location);
    if (detailTarget) return detailTarget;
    const indexTarget = cattleBatchesIndexTarget(location);
    if (indexTarget) return indexTarget;
  }
  if (view === 'list') {
    const detailTarget = broilerBatchesDetailTarget(location);
    if (detailTarget) return detailTarget;
    const indexTarget = broilerBatchesIndexTarget(location);
    if (indexTarget) return indexTarget;
  }
  if (view === 'cattleherds') {
    const herdsTarget = cattleHerdsTarget(location);
    if (herdsTarget) return herdsTarget;
  }
  if (view === 'sheepbatches') {
    const detailTarget = sheepBatchesDetailTarget(location);
    if (detailTarget) return detailTarget;
  }
  if (view === 'sheepflocks') {
    const detailTarget = sheepFlocksDetailTarget(location);
    if (detailTarget) return detailTarget;
  }
  if (view === SHEEP_BATCHES_INDEX_TARGET.view && pathname === SHEEP_BATCHES_INDEX_TARGET.pathname) {
    return SHEEP_BATCHES_INDEX_TARGET;
  }
  if (view === SHEEP_FLOCKS_INDEX_TARGET.view && pathname === SHEEP_FLOCKS_INDEX_TARGET.pathname) {
    return SHEEP_FLOCKS_INDEX_TARGET;
  }
  if (view === 'sheepdailys') {
    const sheepDailyTarget = sheepDailysTarget(location);
    if (sheepDailyTarget) return sheepDailyTarget;
  }
  if (view === 'eggdailys') {
    const eggDailyTarget = eggDailysTarget(location);
    if (eggDailyTarget) return eggDailyTarget;
  }
  if (view === 'pigdailys') {
    const pigDailyTarget = pigDailysTarget(location);
    if (pigDailyTarget) return pigDailyTarget;
  }
  if (view === 'pigbatches') {
    const pigBatchTarget = pigBatchesTarget(location);
    if (pigBatchTarget) return pigBatchTarget;
  }
  if (view === 'broilerdailys') {
    const broilerDailyTarget = broilerDailysTarget(location);
    if (broilerDailyTarget) return broilerDailyTarget;
  }
  if (view === CATTLE_BREEDING_TARGET.view && pathname === CATTLE_BREEDING_TARGET.pathname) {
    return CATTLE_BREEDING_TARGET;
  }
  if (view === CATTLE_DAILYS_INDEX_TARGET.view && pathname === CATTLE_DAILYS_INDEX_TARGET.pathname) {
    return CATTLE_DAILYS_INDEX_TARGET;
  }
  if (view === MY_SUBMISSIONS_TARGET.view && pathname === MY_SUBMISSIONS_TARGET.pathname) {
    return MY_SUBMISSIONS_TARGET;
  }
  if (view === FUEL_SUPPLY_TARGET.view && pathname === FUEL_SUPPLY_TARGET.pathname) {
    return FUEL_SUPPLY_TARGET;
  }
  if (view === EQUIPMENT_FUELING_SUPPLY_TARGET.view && pathname === EQUIPMENT_FUELING_SUPPLY_TARGET.pathname) {
    return EQUIPMENT_FUELING_SUPPLY_TARGET;
  }
  if (view === EQUIPMENT_FUELING_HUB_TARGET.view && pathname === EQUIPMENT_FUELING_HUB_TARGET.pathname) {
    return EQUIPMENT_FUELING_HUB_TARGET;
  }
  if (view === 'fuelingHub') {
    const formTarget = equipmentFuelingFormTarget(pathname);
    if (formTarget) return formTarget;
  }
  if (view === ACTIVITY_TARGET.view && pathname === ACTIVITY_TARGET.pathname) {
    return ACTIVITY_TARGET;
  }
  if (view === PROJECTS_TARGET.view && pathname === PROJECTS_TARGET.pathname) {
    return PROJECTS_TARGET;
  }
  if (view === ADD_FEED_TARGET.view && pathname === ADD_FEED_TARGET.pathname) {
    return ADD_FEED_TARGET;
  }
  if (view === NEWSLETTER_ADMIN_TARGET.view && pathname === NEWSLETTER_ADMIN_TARGET.pathname) {
    return NEWSLETTER_ADMIN_TARGET;
  }
  if (view === CLIENT_ERRORS_TARGET.view && pathname === CLIENT_ERRORS_TARGET.pathname) {
    return CLIENT_ERRORS_TARGET;
  }
  if (view === WEBFORMS_ADMIN_TARGET.view && pathname === WEBFORMS_ADMIN_TARGET.pathname) {
    return WEBFORMS_ADMIN_TARGET;
  }
  if (view === PIG_DAILYS_WEBFORM_TARGET.view && pathname === PIG_DAILYS_WEBFORM_TARGET.pathname) {
    return PIG_DAILYS_WEBFORM_TARGET;
  }
  if (view === 'webformhub') {
    const hubTarget = webformHubDailysTarget(location);
    if (hubTarget) return hubTarget;
  }
  if (view === TASKS_WEBFORM_TARGET.view && pathname === TASKS_WEBFORM_TARGET.pathname) {
    return TASKS_WEBFORM_TARGET;
  }
  if (view === PIG_BREEDING_TARGET.view && pathname === PIG_BREEDING_TARGET.pathname) {
    return PIG_BREEDING_TARGET;
  }
  if (view === 'farrowing') {
    const target = pigFarrowingTarget(location);
    if (target) return target;
  }
  if (view === BROILER_TIMELINE_TARGET.view && pathname === BROILER_TIMELINE_TARGET.pathname) {
    return BROILER_TIMELINE_TARGET;
  }
  if (view === BROILER_FEED_TARGET.view && pathname === BROILER_FEED_TARGET.pathname) {
    return BROILER_FEED_TARGET;
  }
  if (view === PIG_FEED_TARGET.view && pathname === PIG_FEED_TARGET.pathname) {
    return PIG_FEED_TARGET;
  }
  if (view === 'sows') {
    const sowsTarget = pigSowsTarget(location);
    if (sowsTarget) return sowsTarget;
  }
  if (view === CATTLE_LOG_TARGET.view && pathname === CATTLE_LOG_TARGET.pathname) {
    return CATTLE_LOG_TARGET;
  }
  if (view === PASTURE_MAP_TARGET.view && pathname === PASTURE_MAP_TARGET.pathname) {
    return PASTURE_MAP_TARGET;
  }
  if (view === PROCESSING_TARGET.view && pathname === PROCESSING_TARGET.pathname) {
    return PROCESSING_TARGET;
  }
  if (view === 'processingCutSheet') {
    const cutSheetTarget = processingCutSheetTarget(location);
    if (cutSheetTarget) return cutSheetTarget;
  }
  if (view === 'equipmentAdminRecord') {
    const equipmentRecordTarget = equipmentAdminRecordTarget(location);
    if (equipmentRecordTarget) return equipmentRecordTarget;
  }
  if (view === PRODUCTION_TARGET.view && pathname === PRODUCTION_TARGET.pathname) {
    return PRODUCTION_TARGET;
  }
  if (view === CATTLE_HOME_TARGET.view && pathname === CATTLE_HOME_TARGET.pathname) {
    return CATTLE_HOME_TARGET;
  }
  if (view === SHEEP_HOME_TARGET.view && pathname === SHEEP_HOME_TARGET.pathname) {
    return SHEEP_HOME_TARGET;
  }
  if (view === BROILER_HOME_TARGET.view && pathname === BROILER_HOME_TARGET.pathname) {
    return BROILER_HOME_TARGET;
  }
  if (view === LAYERS_HOME_TARGET.view && pathname === LAYERS_HOME_TARGET.pathname) {
    return LAYERS_HOME_TARGET;
  }
  if (view === LAYER_GROUPS_TARGET.view && pathname === LAYER_GROUPS_TARGET.pathname) {
    return LAYER_GROUPS_TARGET;
  }
  if (view === PIGS_HOME_TARGET.view && pathname === PIGS_HOME_TARGET.pathname) {
    return PIGS_HOME_TARGET;
  }
  if (view === WEATHER_TARGET.view && pathname === WEATHER_TARGET.pathname) {
    return WEATHER_TARGET;
  }
  if (view === CATTLE_FORECAST_TARGET.view && pathname === CATTLE_FORECAST_TARGET.pathname) {
    return CATTLE_FORECAST_TARGET;
  }
  if (view === LAYER_BATCHES_INDEX_TARGET.view && pathname === LAYER_BATCHES_INDEX_TARGET.pathname) {
    return LAYER_BATCHES_INDEX_TARGET;
  }
  if (view === 'layerbatches') {
    const housingTarget = layerHousingsDetailTarget(location);
    if (housingTarget) return housingTarget;
    const batchRecordTarget = layerBatchesDetailTarget(location);
    if (batchRecordTarget) return batchRecordTarget;
  }
  if (view === 'layerdailys') {
    const layerDailyTarget = layerDailysTarget(location);
    if (layerDailyTarget) return layerDailyTarget;
  }
  if (view === 'cattledailys') {
    const detailTarget = cattleDailysDetailTarget(pathname);
    if (detailTarget) return detailTarget;
  }
  if (view === 'tasks') {
    const recordTarget = tasksRecordTarget(location);
    if (recordTarget) return recordTarget;
  }
  if (view === LIGHT_HOME_TARGET.view && pathname === LIGHT_HOME_TARGET.pathname) {
    return LIGHT_HOME_TARGET;
  }
  if (view === BROILER_WEIGHINS_TARGET.view && pathname === BROILER_WEIGHINS_TARGET.pathname) {
    return BROILER_WEIGHINS_TARGET;
  }
  if (view === PIG_WEIGHINS_TARGET.view && pathname === PIG_WEIGHINS_TARGET.pathname) {
    return PIG_WEIGHINS_TARGET;
  }
  if (view === CATTLE_WEIGHINS_TARGET.view && pathname === CATTLE_WEIGHINS_TARGET.pathname) {
    return CATTLE_WEIGHINS_TARGET;
  }
  if (view === SHEEP_WEIGHINS_TARGET.view && pathname === SHEEP_WEIGHINS_TARGET.pathname) {
    return SHEEP_WEIGHINS_TARGET;
  }
  if (view === ANIMAL_HISTORY_TARGET.view && pathname === ANIMAL_HISTORY_TARGET.pathname) {
    return ANIMAL_HISTORY_TARGET;
  }
  if (view === WEIGHINS_WEBFORM_TARGET.view && pathname === WEIGHINS_WEBFORM_TARGET.pathname) {
    return WEIGHINS_WEBFORM_TARGET;
  }
  if (view === 'weighinsessions') {
    const sessionRecordTarget = weighInSessionRecordTarget(location);
    if (sessionRecordTarget) return sessionRecordTarget;
  }
  return null;
}

function targetForPath(pathname: string | null, location: unknown = pathname): ReleasedTarget | null {
  const fleetTarget = fleetIndexTarget(location);
  if (fleetTarget) return fleetTarget;
  const fuelingEntryTarget = fleetFuelingEntryTarget(pathname);
  if (fuelingEntryTarget) return fuelingEntryTarget;
  const checklistEntryTarget = fleetChecklistEntryTarget(pathname);
  if (checklistEntryTarget) return checklistEntryTarget;
  const fleetRecordTarget = fleetDetailTarget(pathname);
  if (fleetRecordTarget) return fleetRecordTarget;
  // Kept ahead of the record target: the forecast projection path is a
  // cattle.batches.forecastDetail release, never a cattle.batches.detail one.
  const forecastTarget = cattleForecastBatchDetailTarget(location);
  if (forecastTarget) return forecastTarget;
  const centerTarget = tasksCenterTarget(location);
  if (centerTarget) return centerTarget;
  const detailTarget = cattleBatchesDetailTarget(location);
  if (detailTarget) return detailTarget;
  const batchesIndexTarget = cattleBatchesIndexTarget(location);
  if (batchesIndexTarget) return batchesIndexTarget;
  const broilerBatchDetailTarget = broilerBatchesDetailTarget(location);
  if (broilerBatchDetailTarget) return broilerBatchDetailTarget;
  const broilerBatchIndexTarget = broilerBatchesIndexTarget(location);
  if (broilerBatchIndexTarget) return broilerBatchIndexTarget;
  const herdsTarget = cattleHerdsTarget(location);
  if (herdsTarget) return herdsTarget;
  const sheepBatchTarget = sheepBatchesDetailTarget(location);
  if (sheepBatchTarget) return sheepBatchTarget;
  const sheepAnimalTarget = sheepFlocksDetailTarget(location);
  if (sheepAnimalTarget) return sheepAnimalTarget;
  if (pathname === SHEEP_BATCHES_INDEX_TARGET.pathname) return SHEEP_BATCHES_INDEX_TARGET;
  if (pathname === SHEEP_FLOCKS_INDEX_TARGET.pathname) return SHEEP_FLOCKS_INDEX_TARGET;
  const sheepDailyTarget = sheepDailysTarget(location);
  if (sheepDailyTarget) return sheepDailyTarget;
  const eggDailyTarget = eggDailysTarget(location);
  if (eggDailyTarget) return eggDailyTarget;
  const pigDailyTarget = pigDailysTarget(location);
  if (pigDailyTarget) return pigDailyTarget;
  const pigBatchTarget = pigBatchesTarget(location);
  if (pigBatchTarget) return pigBatchTarget;
  const broilerDailyTarget = broilerDailysTarget(location);
  if (broilerDailyTarget) return broilerDailyTarget;
  if (pathname === CATTLE_BREEDING_TARGET.pathname) return CATTLE_BREEDING_TARGET;
  if (pathname === CATTLE_DAILYS_INDEX_TARGET.pathname) return CATTLE_DAILYS_INDEX_TARGET;
  if (pathname === MY_SUBMISSIONS_TARGET.pathname) return MY_SUBMISSIONS_TARGET;
  if (pathname === FUEL_SUPPLY_TARGET.pathname) return FUEL_SUPPLY_TARGET;
  if (pathname === EQUIPMENT_FUELING_SUPPLY_TARGET.pathname) return EQUIPMENT_FUELING_SUPPLY_TARGET;
  if (pathname === EQUIPMENT_FUELING_HUB_TARGET.pathname) return EQUIPMENT_FUELING_HUB_TARGET;
  const formTarget = equipmentFuelingFormTarget(pathname);
  if (formTarget) return formTarget;
  if (pathname === ACTIVITY_TARGET.pathname) return ACTIVITY_TARGET;
  if (pathname === PROJECTS_TARGET.pathname) return PROJECTS_TARGET;
  if (pathname === ADD_FEED_TARGET.pathname) return ADD_FEED_TARGET;
  if (pathname === NEWSLETTER_ADMIN_TARGET.pathname) return NEWSLETTER_ADMIN_TARGET;
  if (pathname === CLIENT_ERRORS_TARGET.pathname) return CLIENT_ERRORS_TARGET;
  if (pathname === WEBFORMS_ADMIN_TARGET.pathname) return WEBFORMS_ADMIN_TARGET;
  if (pathname === PIG_DAILYS_WEBFORM_TARGET.pathname) return PIG_DAILYS_WEBFORM_TARGET;
  const hubTarget = webformHubDailysTarget(location);
  if (hubTarget) return hubTarget;
  if (pathname === TASKS_WEBFORM_TARGET.pathname) return TASKS_WEBFORM_TARGET;
  if (pathname === PIG_BREEDING_TARGET.pathname) return PIG_BREEDING_TARGET;
  const farrowingTarget = pigFarrowingTarget(location);
  if (farrowingTarget) return farrowingTarget;
  if (pathname === BROILER_TIMELINE_TARGET.pathname) return BROILER_TIMELINE_TARGET;
  if (pathname === BROILER_FEED_TARGET.pathname) return BROILER_FEED_TARGET;
  if (pathname === PIG_FEED_TARGET.pathname) return PIG_FEED_TARGET;
  const sowsTarget = pigSowsTarget(location);
  if (sowsTarget) return sowsTarget;
  if (pathname === CATTLE_LOG_TARGET.pathname) return CATTLE_LOG_TARGET;
  if (pathname === PASTURE_MAP_TARGET.pathname) return PASTURE_MAP_TARGET;
  if (pathname === PROCESSING_TARGET.pathname) return PROCESSING_TARGET;
  const cutSheetTarget = processingCutSheetTarget(location);
  if (cutSheetTarget) return cutSheetTarget;
  const equipmentRecordTarget = equipmentAdminRecordTarget(location);
  if (equipmentRecordTarget) return equipmentRecordTarget;
  if (pathname === PRODUCTION_TARGET.pathname) return PRODUCTION_TARGET;
  if (pathname === CATTLE_HOME_TARGET.pathname) return CATTLE_HOME_TARGET;
  if (pathname === SHEEP_HOME_TARGET.pathname) return SHEEP_HOME_TARGET;
  if (pathname === BROILER_HOME_TARGET.pathname) return BROILER_HOME_TARGET;
  if (pathname === LAYERS_HOME_TARGET.pathname) return LAYERS_HOME_TARGET;
  if (pathname === LAYER_GROUPS_TARGET.pathname) return LAYER_GROUPS_TARGET;
  if (pathname === PIGS_HOME_TARGET.pathname) return PIGS_HOME_TARGET;
  if (pathname === WEATHER_TARGET.pathname) return WEATHER_TARGET;
  if (pathname === CATTLE_FORECAST_TARGET.pathname) return CATTLE_FORECAST_TARGET;
  if (pathname === LAYER_BATCHES_INDEX_TARGET.pathname) return LAYER_BATCHES_INDEX_TARGET;
  const layerHousingTarget = layerHousingsDetailTarget(location);
  if (layerHousingTarget) return layerHousingTarget;
  const layerBatchRecordTarget = layerBatchesDetailTarget(location);
  if (layerBatchRecordTarget) return layerBatchRecordTarget;
  const layerDailyTarget = layerDailysTarget(location);
  if (layerDailyTarget) return layerDailyTarget;
  const dailysDetailTarget = cattleDailysDetailTarget(pathname);
  if (dailysDetailTarget) return dailysDetailTarget;
  const tasksRecord = tasksRecordTarget(location);
  if (tasksRecord) return tasksRecord;
  if (pathname === LIGHT_HOME_TARGET.pathname) return LIGHT_HOME_TARGET;
  if (pathname === BROILER_WEIGHINS_TARGET.pathname) return BROILER_WEIGHINS_TARGET;
  if (pathname === PIG_WEIGHINS_TARGET.pathname) return PIG_WEIGHINS_TARGET;
  if (pathname === CATTLE_WEIGHINS_TARGET.pathname) return CATTLE_WEIGHINS_TARGET;
  if (pathname === SHEEP_WEIGHINS_TARGET.pathname) return SHEEP_WEIGHINS_TARGET;
  if (pathname === ANIMAL_HISTORY_TARGET.pathname) return ANIMAL_HISTORY_TARGET;
  if (pathname === WEIGHINS_WEBFORM_TARGET.pathname) return WEIGHINS_WEBFORM_TARGET;
  const sessionRecordTarget = weighInSessionRecordTarget(location);
  if (sessionRecordTarget) return sessionRecordTarget;
  return null;
}

function releasedResolutionForTarget(
  input: ReleasedSurfaceBoundaryInput,
  target: ReleasedTarget,
): ReleasedResolution | null {
  if (!input || typeof input !== 'object' || !isCurrentLease(input.lease)) {
    return null;
  }

  const lease = input.lease;
  const resolution = resolveAuthenticatedSurface({
    view: target.view,
    location: target.pathname,
    showForm: input.showForm,
    editId: input.editId,
    authState: input.authState,
    sessionEpoch: lease.sessionEpoch,
    leaseEpoch: lease.sessionEpoch,
    leaseUserId: lease.userId,
    ledger: BOOT_RELEASE_LEDGER,
  });
  if (
    resolution.mode !== AUTHENTICATED_SURFACE_MODE.RELEASED ||
    resolution.surface !== target.surface ||
    resolution.routedPathname !== target.pathname
  ) {
    return null;
  }
  return resolution;
}

function homeResolutionForPath(input: ReleasedSurfaceBoundaryInput): ReleasedResolution | null {
  if (pathnameFrom(input?.location) !== RELEASED_STANDARD_HOME_PATHNAME) return null;

  // The candidate order is not a role classifier. Each attempt asks the same
  // authenticated resolver which surface the current identity may render, then
  // requires that answer to equal the candidate it is considering. A standard
  // identity therefore cannot borrow the Light target, and a Light identity
  // cannot borrow the standard renderer, despite the shared raw root path.
  return (
    releasedResolutionForTarget(input, STANDARD_HOME_TARGET) ?? releasedResolutionForTarget(input, LIGHT_HOME_TARGET)
  );
}

function releasedResolution(input: ReleasedSurfaceBoundaryInput): ReleasedResolution | null {
  const fullscreenTarget = broilerBatchFormFullscreenTarget(input);
  if (fullscreenTarget) {
    const fullscreenResolution = releasedResolutionForTarget(input, fullscreenTarget);
    if (fullscreenResolution) return fullscreenResolution;
  }

  if (input?.view === STANDARD_HOME_TARGET.view) {
    const homeResolution = homeResolutionForPath(input);
    if (homeResolution) return homeResolution;
  }
  const target = exactTargetForPair(input?.view, pathnameFrom(input?.location), input?.location);
  return target ? releasedResolutionForTarget(input, target) : null;
}

/**
 * A raw URL/view mismatch is never a release claim. During a normal URL/view
 * synchronization frame, however, starting a legacy run would eagerly fetch
 * `app_store` before the matching state commits. Hold that frame only when a
 * separately re-resolved, current authorized target proves the old browser
 * path belongs to an audited release named by BOOT_RELEASE_LEDGER. That count
 * used to be transcribed here as a word, and it went stale silently in three
 * consecutive waves because nothing asserts a comment; naming the ledger
 * removes the drift instead of resetting it. This guards direct loads
 * and released-to-released navigation only; a released view paired with an
 * ordinary legacy or alias path remains legacy immediately.
 */
export function isReleasedSurfaceRouteSyncPending(input: ReleasedSurfaceBoundaryInput): boolean {
  if (!input || typeof input !== 'object' || !isCurrentLease(input.lease)) return false;

  const pathname = pathnameFrom(input.location);
  if (exactTargetForPair(input.view, pathname, input.location)) return false;

  // A root synchronization frame can still carry the prior view. Re-resolve
  // both audited Home candidates before the generic pathname target: the latter
  // has no identity dimension and intentionally keeps the historic Light row.
  if (homeResolutionForPath(input)) return true;

  const pathTarget = targetForPath(pathname, input.location);
  if (pathTarget && releasedResolutionForTarget(input, pathTarget)) return true;
  return false;
}

function rawAuthenticatedResolution(input: ReleasedSurfaceBoundaryInput): AuthenticatedSurfaceResolution | null {
  if (!input || typeof input !== 'object' || !isCurrentLease(input.lease)) return null;

  const lease = input.lease;
  return resolveAuthenticatedSurface({
    view: input.view,
    location: input.location,
    showForm: input.showForm,
    editId: input.editId,
    authState: input.authState,
    sessionEpoch: lease.sessionEpoch,
    leaseEpoch: lease.sessionEpoch,
    leaseUserId: lease.userId,
    ledger: BOOT_RELEASE_LEDGER,
  });
}

/**
 * Classify the current authenticated raw route after the global loader's
 * retirement.  This is intentionally a disposition rather than another
 * release capability: a rejected raw pathname must be visible to App so it can
 * render recovery instead of silently falling through to the historic router.
 *
 * `/dailys/` is the one compatibility spelling proved equivalent to the hub
 * selector.  Its exact sibling `/dailys/tasks` has its own released target,
 * and arbitrary/encoded descendants remain terminal rather than being widened
 * into the hub by an optimistic redirect.
 */
export function resolveAuthenticatedRouteDisposition(
  input: ReleasedSurfaceBoundaryInput,
): AuthenticatedRouteDisposition {
  if (!input || typeof input !== 'object' || !isCurrentLease(input.lease)) {
    return Object.freeze({kind: 'terminal', reason: 'invalid-lease'});
  }

  if (releasedResolution(input)) return Object.freeze({kind: 'released'});
  if (isReleasedSurfaceRouteSyncPending(input)) return Object.freeze({kind: 'sync-pending'});

  const resolution = rawAuthenticatedResolution(input);
  const pathname = pathnameFrom(input.location);
  if (
    pathname === `${WEBFORM_HUB_DAILYS_PATHNAME}/` &&
    resolution?.mode === AUTHENTICATED_SURFACE_MODE.LEGACY &&
    resolution.reason === AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE
  ) {
    return Object.freeze({kind: 'redirect', pathname: WEBFORM_HUB_DAILYS_PATHNAME});
  }

  return Object.freeze({
    kind: 'terminal',
    reason: resolution?.reason ?? AUTHENTICATED_SURFACE_FALLBACK.MALFORMED_INPUT,
  });
}

/**
 * Mint the independent-render capability for the current resolved route. A
 * non-released, malformed, denied, stale, or unmapped candidate returns null,
 * leaving App to select synchronization, redirect, or terminal recovery.
 */
export function claimReleasedSurfaceBoundary(input: ReleasedSurfaceBoundaryInput): ReleasedSurfaceClaim | null {
  const resolution = releasedResolution(input);
  const lease = input && typeof input === 'object' ? input.lease : null;
  if (!resolution || !isCurrentLease(lease)) return null;

  return Object.freeze({
    kind: 'released-surface',
    userId: lease.userId,
    sessionEpoch: lease.sessionEpoch,
    runId: lease.runId,
    resolution,
  });
}

/**
 * A live lease alone is insufficient: re-run the resolver against the current
 * render snapshot. This makes a released -> non-released transition fail
 * closed in the render itself, before the effect selects its recovery
 * disposition.
 */
export function isCurrentReleasedSurfaceClaim(
  claim: ReleasedSurfaceClaim | null | undefined,
  input: ReleasedSurfaceBoundaryInput,
): claim is ReleasedSurfaceClaim {
  if (!claim || typeof claim !== 'object' || claim.kind !== 'released-surface') return false;
  const lease = input && typeof input === 'object' ? input.lease : null;
  if (!isCurrentLease(lease)) return false;
  if (claim.userId !== lease.userId || claim.sessionEpoch !== lease.sessionEpoch || claim.runId !== lease.runId) {
    return false;
  }

  const resolution = releasedResolution(input);
  return Boolean(
    resolution &&
    resolution.surface === claim.resolution.surface &&
    resolution.routedPathname === claim.resolution.routedPathname,
  );
}

export default claimReleasedSurfaceBoundary;
